import type { FApolloImageParams } from '~api';
import { MimeType } from '~types';

type FapolloImagePrettyParams = {
  format: FApolloImageParams['f'];
  quality: FApolloImageParams['q'];
  rotation: FApolloImageParams['r'];
  size: FApolloImageParams['s'];
  profiles: FApolloImageParams['p'];
};

const DEFAULT_PARAMS: Pick<FapolloImagePrettyParams, 'quality' | 'format'> = {
  quality: 80,
  format: 'webp',
};
// List of supported Device Pixel Ratios
const DPR_LIST = [1, 2, 3];

const mapFormatToMimeType: Record<FapolloImagePrettyParams['format'], MimeType> = {
  jpeg: MimeType.IMAGE_JPEG,
  webp: MimeType.IMAGE_WEBP,
  png: MimeType.IMAGE_PNG,
};

const mapPrettyKeyToApolloCDNParamKey: Record<
  keyof FapolloImagePrettyParams,
  keyof FApolloImageParams
> = {
  format: 'f',
  quality: 'q',
  rotation: 'r',
  size: 's',
  profiles: 'p',
};

export const buildImageURL = (
  url: string,
  params?: Partial<Omit<FapolloImagePrettyParams, 'profiles'>>,
) =>
  `${url}${Object.entries({ ...DEFAULT_PARAMS, ...params }).reduce((acc, [prettyKey, value]) => {
    const key = mapPrettyKeyToApolloCDNParamKey[prettyKey as keyof FapolloImagePrettyParams];
    let param = '';

    if (key !== 'q' || (key === 'q' && params?.format !== 'png')) {
      param = `${key}=${value}`;
    } else {
      return acc;
    }

    return `${acc};${param}`;
  }, '')}`;

type ApolloCDNSourceElementConfig = {
  src: string;
  type: MimeType;
  srcSet?: string;
};

export const buildResponsiveProps = (
  url: string,
  assetWidths?: number | Array<number>,
  params?: Partial<Omit<FapolloImagePrettyParams, 'profiles' | 'size'>>,
): ApolloCDNSourceElementConfig => {
  const srcSetArray: Array<number> = [];

  return {
    src: buildImageURL(url, params),
    type: mapFormatToMimeType[params?.format ?? DEFAULT_PARAMS.format],
    ...(!!assetWidths && {
      srcSet: srcSetArray
        .concat(assetWidths ?? [])
        .flatMap((width) =>
          DPR_LIST.map((dpr) => {
            const scaledWidth = dpr * width;

            return `${buildImageURL(url, {
              ...params,
              size: `${scaledWidth}x0`,
            })} ${scaledWidth}w`;
          }),
        )
        .join(', '),
    }),
  };
};
